import { Component, type ReactNode } from 'react';
import type { ExpandedItems } from '../../../../../../model/table';
import { ITEM_TYPE } from '../common/constants';
import { getLastTopLevelFlatIndex } from '../common/get-flat-indices';
import { getHierarchyEnrichedItems } from '../common/get-hierarchy-enriched-items';
import type {
	GenericItem,
	GenericChildItem,
	RenderItem,
	RenderItemContainer,
	ItemContainerMeta,
} from '../common/types';

type Props<ChildItemType extends GenericChildItem, ItemType extends GenericItem<ChildItemType>> = {
	items: ItemType[];
	expandedItems: ExpandedItems;
	activeLevel: number;
	renderItem: RenderItem<ChildItemType, ItemType>;
	renderItemContainer: RenderItemContainer<ChildItemType, ItemType>;
};

const defaultRenderItemContainer = <
	ChildItemType extends GenericChildItem,
	ItemType extends GenericItem<ChildItemType>,
>(
	item: ItemType,
	meta: ItemContainerMeta,
	index: number,
	children: ReactNode,
) => children;

/* A centralised component that encapsulates the logic for flattening the item hierarchy
 * so its' structure can be shared between the list and the chart. Metadata is derived from
 * the structure and passed to each item to retain the context of the hierarchy.
 */
// eslint-disable-next-line jira/react/no-class-components
class FlatItems<
	ChildItemType extends GenericChildItem,
	ItemType extends GenericItem<ChildItemType>,
> extends Component<Props<ChildItemType, ItemType>> {
	static defaultProps = {
		renderItemContainer: defaultRenderItemContainer,
	};

	render() {
		const { items, expandedItems, activeLevel, renderItem, renderItemContainer } = this.props;

		const enrichedItems = getHierarchyEnrichedItems(items, expandedItems, activeLevel);

		const itemContainerMeta = {
			lastItemIndex: getLastTopLevelFlatIndex(items, expandedItems),
		};

		const flatItemElements: Array<ReactNode> = [];

		enrichedItems.forEach((enrichedItem) => {
			// Group an item and its children inside a container element
			if (enrichedItem.type === ITEM_TYPE) {
				const { item, childItems, meta, flatIndex } = enrichedItem;
				const itemContainerChildren = [renderItem(item, meta, flatIndex)];

				childItems.forEach((childItem) => {
					itemContainerChildren.push(
						// @ts-expect-error - TS2345 - Argument of type 'GenericChildItem' is not assignable to parameter of type 'ChildItemType | ItemType'.
						renderItem(childItem.item, childItem.meta, childItem.flatIndex),
					);
				});

				flatItemElements.push(
					renderItemContainer(item, itemContainerMeta, flatIndex, itemContainerChildren),
				);
			}
		});

		return flatItemElements;
	}
}

export default FlatItems;
